"use client";

import * as React from "react";

/**
 * 3컬럼 셸(ArticleShell)의 사이드바 슬롯을 자식(PostLayout 내부의 목차·필기·출석)에게
 * 내려주는 Context (JCG-2063 [D] → JCG-2140 도메인 분할).
 *
 * 재배치 대상(TableOfContents / Note / AttendanceButton 등)은 server component 인 PostLayout
 * (= ArticleShell 의 children) 안쪽 깊숙이 렌더되므로, 만든 element 를 부모 aside 로 끌어올리는
 * lift-up 이 불가능하다. 대신 셸이 사이드바에 portal 타깃 div 를 두고 그 DOM element 만
 * Context 로 내려주면, 각 컴포넌트가 `createPortal` 로 자기 자신을 해당 슬롯에 그릴 수 있다.
 *
 * - Provider 가 없으면(클래식/모바일 레이아웃) 기본값 `inShell:false` 가 반환되어
 *   각 컴포넌트는 기존 floating/fixed 동작을 그대로 유지한다(SSR-safe / no-JS 폴백 계약).
 * - 슬롯 element 는 마운트 타이밍 때문에 `useState` 로 노출한다(ref 콜백 setter 사용).
 *   첫 렌더에는 null → 셸 마운트 후 setState → consumer 재렌더 시 portal 그림.
 *
 * ## JCG-2140 도메인 분할
 *
 * 기존엔 30여 개 필드를 한 평면 Context(ArticleShellSlots)에 담아, 우측 컬럼의
 * "panels / solution / compose / custom / none" 모드가 if/else 로 암묵 인코딩돼 있었다.
 * 이제 관심사를 둘로 나눈다:
 *
 * 1. **{@link SlotRegistryContext}** — 영역(left / right / header) 기반 named record 로 정적
 *    portal 슬롯을 그룹화한다. 모드와 무관하게 항상 같은 자리에 있는 슬롯들.
 * 2. **{@link SolutionModeContext}** — 우측 컬럼의 변신 모드(풀이/작성)를 상태+액션으로 분리.
 *
 * 기존 19개 소비처는 {@link useArticleShellSlots} 호환 어댑터로 무수정 유지한다(점진 이주).
 */

// ---------------------------------------------------------------------------
// 영역 기반 슬롯 레지스트리 (JCG-2140)
// ---------------------------------------------------------------------------

/** portal 타깃 슬롯(없으면 null) */
export type Slot = HTMLElement | null;

/**
 * 좌측 사이드바에 그려지는 정적 슬롯들(모드 무관). 셸 변형마다 일부만 채워진다.
 * - 일반 글: tocSlot(목차)
 * - 기출·퀴즈: scoreSlot(점수) + problemNavSlot(문제 목록)
 * - 카테고리 인덱스: subjectSlot(주요항목)
 * - custom 셸: customLeftSlot
 * - 복원 게시판: restorationLeftSlot(진행바·문제 네비)
 */
export interface LeftSlots {
  /** 좌측 사이드바 목차 슬롯 (starter↑) */
  tocSlot: Slot;
  /** 좌측 사이드바 점수 슬롯 (기출·퀴즈) */
  scoreSlot: Slot;
  /** 좌측 사이드바 문제 목록 네비 슬롯 (퀴즈·기출, 목차 자리) */
  problemNavSlot: Slot;
  /** 좌측 사이드바 주요항목 슬롯 (카테고리 인덱스) */
  subjectSlot: Slot;
  /** custom 셸 좌측 사이드바 슬롯 */
  customLeftSlot: Slot;
  /** 복원 게시판 좌측 슬롯 — 진행바 + 문제 네비 */
  restorationLeftSlot: Slot;
}

/**
 * 우측 사이드바에 그려지는 정적 슬롯들(모드 무관). 풀이/작성 모드 슬롯은 여기 두지 않고
 * {@link SolutionMode} 가 소유한다(모드별 변신이라 관심사가 다름).
 */
export interface RightSlots {
  /** 우측 사이드바 필기 슬롯 (starter↑) */
  noteSlot: Slot;
  /** 우측 사이드바 출석 슬롯 (이론 학습 글) */
  attendanceSlot: Slot;
  /** 우측 사이드바 문제 보기 설정 슬롯 (필기 위, 퀴즈·기출) */
  quizControlsSlot: Slot;
  /** custom 셸 우측 사이드바 슬롯 */
  customRightSlot: Slot;
  /** 복원 게시판 우측 슬롯 — 도구(작성 진입·로그인·통계·admin) */
  restorationRightSlot: Slot;
}

/** 상단 바(ContentHeader) 슬롯. */
export interface HeaderSlots {
  /** 상단 바 중앙 슬롯 — 복원 회차 셀렉터 */
  shellHeaderSlot: Slot;
  /** 상단 바 우측 액션 슬롯 — 글 페이지 언어 토글 등 */
  headerActionSlot: Slot;
}

/**
 * 출석 ↔ 지도 동기화 신호 (JCG-2065). 출석 토글 시 버전을 올려 좌측 지도(TrailMap)가
 * 출석 표시를 재조회하게 한다.
 */
export interface AttendanceSync {
  /** 출석 갱신 버전(증가 시 지도 재조회 트리거) */
  attendanceVersion: number;
  /** 출석 토글 후 지도 갱신을 요청 */
  refreshAttendance: () => void;
}

/**
 * 좌측 사이드바 Section 노출 신호 — 본문 컴포넌트가 마운트/언마운트 시 토글해 셸이
 * 점수/문제설정 Section 을 노출할지 결정한다(JCG-2066).
 */
export interface SectionSignals {
  /** 점수 Section 노출 신호 — ArticleScorePanel 마운트/언마운트 시 토글 */
  setScoreActive: (active: boolean) => void;
  /** 문제 보기 설정 슬롯 노출 신호 — RandomProblems 필터 컨트롤 마운트/언마운트 시 토글 */
  setControlsActive: (active: boolean) => void;
}

/**
 * 영역 기반 슬롯 레지스트리 Context 값. 셸이 만든 portal 타깃과 cross-cutting 신호를 묶는다.
 * 우측 변신 모드(풀이/작성)는 {@link SolutionModeContext} 에 분리.
 */
export interface SlotRegistry {
  /** 셸(데스크탑 글 페이지) 안에서 렌더 중인지 여부 */
  inShell: boolean;
  left: LeftSlots;
  right: RightSlots;
  header: HeaderSlots;
  attendance: AttendanceSync;
  sections: SectionSignals;
}

const EMPTY_LEFT: LeftSlots = {
  tocSlot: null,
  scoreSlot: null,
  problemNavSlot: null,
  subjectSlot: null,
  customLeftSlot: null,
  restorationLeftSlot: null,
};

const EMPTY_RIGHT: RightSlots = {
  noteSlot: null,
  attendanceSlot: null,
  quizControlsSlot: null,
  customRightSlot: null,
  restorationRightSlot: null,
};

const EMPTY_HEADER: HeaderSlots = {
  shellHeaderSlot: null,
  headerActionSlot: null,
};

const DEFAULT_REGISTRY: SlotRegistry = {
  inShell: false,
  left: EMPTY_LEFT,
  right: EMPTY_RIGHT,
  header: EMPTY_HEADER,
  attendance: { attendanceVersion: 0, refreshAttendance: () => {} },
  sections: { setScoreActive: () => {}, setControlsActive: () => {} },
};

export const SlotRegistryContext =
  React.createContext<SlotRegistry>(DEFAULT_REGISTRY);

export function useSlotRegistry(): SlotRegistry {
  return React.useContext(SlotRegistryContext);
}

/**
 * 슬롯이 없는 빈 레지스트리를 만든다(JCG-2140). 풀이 모드만 쓰는 경량 셸(ForumProblemShell)이
 * 좌/우/헤더 슬롯·신호를 일일이 null/noop 으로 박제하지 않고 이 팩토리로 채우게 한다.
 * @param inShell 셸 안에서 렌더 중인지 — 풀이 모드 진입(ProblemAnswer)을 위해 true 로 둔다.
 */
export function emptySlotRegistry(inShell: boolean): SlotRegistry {
  // 슬롯·신호가 전부 불변(null/noop)이라 DEFAULT_REGISTRY 참조를 그대로 공유해도 안전하다.
  return { ...DEFAULT_REGISTRY, inShell };
}

// ---------------------------------------------------------------------------
// 풀이/작성 모드 Context (JCG-2140)
// ---------------------------------------------------------------------------

/**
 * 우측 컬럼 변신 모드(풀이/작성)의 상태 + 액션. 슬롯 레지스트리와 분리해 "우측이 무엇으로
 * 변신했는가"라는 관심사를 한 곳에 모은다.
 *
 * - rightMode: 우측 컬럼 모드(panels|solution|compose). 풀이/작성이 아니면 "panels".
 * - solutionSlot/errorNoteSlot: 풀이 모드일 때 풀이 본문/오답노트 portal 타깃.
 * - composeSlot: 작성 모드(복원 게시판)일 때 PostEditor portal 타깃.
 * - enter/exit 액션은 어댑터 경유 호환 유지(JCG-2140 필수 보존).
 *
 * `activeSolutionId === panelId` 가드는 ProblemAnswer 쪽에 그대로 둔다(다중 ProblemAnswer/
 * RandomizedProblems 풀이 교체 회귀 방지).
 */
export interface SolutionMode {
  /** 우측 사이드바 모드 */
  rightMode: "panels" | "solution" | "compose";
  /** 풀이 모드일 때 풀이 본문 슬롯 (위 pane) */
  solutionSlot: Slot;
  /** 풀이 모드 + 오답노트 열림일 때 오답노트 슬롯 (아래 pane) */
  errorNoteSlot: Slot;
  /** 현재 풀이가 열린 문제 id (= ProblemAnswer 의 id ?? componentId) */
  activeSolutionId: string | null;
  /** 오답노트 50/50 분할 열림 여부 (셸 단일 소유) */
  wrongOpen: boolean;
  /** 풀이 "숨기기" 레일 상태 (셸 단일 소유) */
  solutionCollapsed: boolean;
  /** 풀이 모드 진입 — 우측 변신. 다른 문제로 호출 시 교체 + 오답노트 리셋 */
  enterSolution: (id: string) => void;
  /** 풀이 모드 종료 — 학습 도구 복귀. 좌측 메뉴 상태는 변경하지 않음 */
  exitSolution: () => void;
  /** 오답노트 분할 열기/닫기 */
  setWrongOpen: (open: boolean) => void;
  /** 풀이/오답노트 세로 분할 비율(위 풀이 비율) 직접 설정 — 오답노트 복습에서 저장된 사용자
   *  조절값이 없을 때만 0.5(풀이 있음)/0.1(풀이 없음·노트 90%) 기본값을 적용(JCG-2060/2856). */
  setSplitRatio: (ratio: number) => void;
  /** 작성 모드(compose)일 때 PostEditor 슬롯 — 우측 사이드바가 넓게 확장됨 */
  composeSlot: Slot;
  /** 작성 모드 진입 — 우측 사이드바를 에디터로 변신 + 폭 확장 */
  enterCompose: () => void;
  /** 작성 모드 종료 — 도구 모드로 복귀 */
  exitCompose: () => void;
}

const DEFAULT_SOLUTION_MODE: SolutionMode = {
  rightMode: "panels",
  solutionSlot: null,
  errorNoteSlot: null,
  activeSolutionId: null,
  wrongOpen: false,
  solutionCollapsed: false,
  enterSolution: () => {},
  exitSolution: () => {},
  setWrongOpen: () => {},
  setSplitRatio: () => {},
  composeSlot: null,
  enterCompose: () => {},
  exitCompose: () => {},
};

export const SolutionModeContext =
  React.createContext<SolutionMode>(DEFAULT_SOLUTION_MODE);

export function useSolutionMode(): SolutionMode {
  return React.useContext(SolutionModeContext);
}

/** 풀이 모드만 쓰는 셸이 채울 입력 — compose(작성)는 미지원이라 팩토리가 noop 으로 채운다. */
export type SolutionOnlyInput = Pick<
  SolutionMode,
  | "rightMode"
  | "solutionSlot"
  | "errorNoteSlot"
  | "activeSolutionId"
  | "wrongOpen"
  | "solutionCollapsed"
  | "enterSolution"
  | "exitSolution"
  | "setWrongOpen"
>;

const COMPOSE_NOOP = {
  composeSlot: null,
  enterCompose: () => {},
  exitCompose: () => {},
} satisfies Pick<SolutionMode, "composeSlot" | "enterCompose" | "exitCompose">;

/**
 * 풀이 모드만 쓰는 경량 셸(ForumProblemShell, JCG-2140)이 작성 모드(compose) 필드를 일일이
 * null/noop 으로 박제하지 않도록, 풀이 관련 필드만 받아 compose 는 공유 noop 으로 채워준다.
 */
export function solutionOnlyMode(input: SolutionOnlyInput): SolutionMode {
  // setSplitRatio 는 오답노트 복습(JCG-2060) 전용이라 경량 셸(forum)에선 noop 으로 채운다
  // (forum ProblemAnswer 는 복습 모드가 아니라 setSplitRatio 를 호출하지 않음).
  return { setSplitRatio: () => {}, ...input, ...COMPOSE_NOOP };
}

// ---------------------------------------------------------------------------
// 우측 컬럼 discriminated union (JCG-2141)
// ---------------------------------------------------------------------------

/**
 * ArticleShell 우측 컬럼이 "무엇으로 렌더되는가"를 타입안전하게 표현하는 discriminated
 * union (JCG-2141). 기존엔 `rightMode` 문자열 + `isSolution`/`isCompose`/`isRestoration`
 * 불리언 조합이 if/else 와 `rightWidthCss` 삼항으로 흩어져, 불가능한 상태(solution+compose
 * 동시 등)가 타입으로 차단되지 않았다. 이제 `kind` 단일 판별자로 정리하고, 각 kind 가 렌더에
 * 필요한 폭/리사이즈/배경 정보를 페이로드로 들고 있어 ArticleShell 의 우측 분기가 단일 switch
 * (+ `default: never` exhaustiveness)로 환원된다.
 *
 * ## kind 우선순위 (deriveRightColumn)
 * `compose` > `solution` > `custom` > `panels` > `none`
 * - compose: 작성 모드(복원 게시판 PostEditor). `rightMode==='compose'` 이면서 복원 페이지일 때만.
 * - solution: 풀이 모드(ProblemAnswer 풀이/오답노트). `rightMode==='solution'`.
 * - custom: 인덱스 셸(/forum/jcg)·/my·홈 대시보드 — variant='custom' 의 우측 맥락 슬롯.
 * - panels: 학습 도구(필기·출석)·복원 도구·광고 — 기본 우측 컬럼.
 * - none: 우측 컬럼 자체가 없음(2컬럼: /my 대시보드 홈 등 hideRight).
 *
 * 폭 정보는 CSS 문자열(`widthCss`)로 들고 와 ShellFrame 의 `rightWidthCss` 에 그대로 주입한다.
 * 배경/리사이즈 핸들 노출도 kind 에서 파생되므로 호출부의 삼항 조합을 제거한다.
 */
export type RightColumn =
  | {
      kind: "compose";
      /** 우측 폭 CSS (작성 모드 폭, 예: `${composeW}px`) */
      widthCss: string;
      /** 우측 aside 배경(작성/풀이 모드 = 회색) */
      gray: boolean;
      /** 리사이즈 핸들 노출 */
      showResize: boolean;
    }
  | {
      kind: "solution";
      widthCss: string;
      gray: boolean;
      showResize: boolean;
    }
  | {
      kind: "custom";
      widthCss: string;
      gray: false;
      showResize: boolean;
    }
  | {
      kind: "panels";
      widthCss: string;
      gray: false;
      showResize: boolean;
    }
  | {
      kind: "none";
    };

/**
 * deriveRightColumn 입력 — `(registry, mode)` 의 모드 판별에 더해, ArticleShell 만 아는
 * 셸 컨텍스트(variant/복원 여부/모드별 폭/접힘 상태)를 받아 우측 컬럼 union 을 단일 소스로 만든다.
 * registry.inShell 이 false(셸 밖)면 우측 컬럼 개념이 없으므로 항상 `none`.
 */
export interface RightColumnInput {
  /** variant='custom' 셸(인덱스/대시보드) 여부 */
  isCustom: boolean;
  /** custom 셸에서 우측 컬럼을 숨기는 2컬럼(예: /my 대시보드 홈) 여부 */
  hideRight: boolean;
  /** 복원 게시판 페이지 여부 — compose 유효성 가드(누수 1프레임 방어) */
  isRestoration: boolean;
  /** 풀이 "숨기기"(레일) 여부 */
  solutionCollapsed: boolean;
  /** 모드별 폭 CSS 후보 */
  widths: {
    /** 작성 모드 폭 (예: `${composeW}px`) */
    compose: string;
    /** 풀이 모드 폭 (숨기기 시 레일 폭 이미 반영, 예: `${solutionW}px` / `${SOLUTION_RAIL}px`) */
    solution: string;
    /** 학습 도구/custom 기본 폭 (예: `${rightW}px`) */
    base: string;
  };
}

/**
 * registry + mode + 셸 컨텍스트로부터 우측 컬럼 union 을 도출한다(JCG-2141).
 * 우선순위: compose > solution > custom > panels > none.
 *
 * - 셸 밖(`!registry.inShell`)이면 우측 컬럼 없음 → `none`.
 * - compose 는 `rightMode==='compose'` 이면서 복원 페이지일 때만(누수 방어 — ArticleShell 의
 *   `isCompose = rightMode==='compose' && isRestoration` 가드와 동치).
 * - custom 셸은 hideRight 면 `none`(2컬럼), 아니면 `custom`.
 */
export function deriveRightColumn(
  registry: Pick<SlotRegistry, "inShell">,
  mode: Pick<SolutionMode, "rightMode">,
  input: RightColumnInput,
): RightColumn {
  if (!registry.inShell) return { kind: "none" };

  // compose 는 복원 페이지에서만 유효 — 누수 방어(1프레임 가드 동치).
  if (mode.rightMode === "compose" && input.isRestoration) {
    return {
      kind: "compose",
      widthCss: input.widths.compose,
      gray: true,
      showResize: true,
    };
  }

  if (mode.rightMode === "solution") {
    return {
      kind: "solution",
      widthCss: input.widths.solution,
      gray: true,
      // 레일(숨기기)일 땐 폭 고정이라 리사이즈 핸들 숨김.
      showResize: !input.solutionCollapsed,
    };
  }

  if (input.isCustom) {
    return input.hideRight
      ? { kind: "none" }
      : {
          kind: "custom",
          widthCss: input.widths.base,
          gray: false,
          showResize: true,
        };
  }

  return {
    kind: "panels",
    widthCss: input.widths.base,
    gray: false,
    showResize: true,
  };
}

// ---------------------------------------------------------------------------
// 호환 어댑터 — 기존 평면 ArticleShellSlots 소비처(19곳) 무수정 유지 (JCG-2140)
// ---------------------------------------------------------------------------

/**
 * 기존 평면 슬롯 인터페이스. JCG-2140 에서 {@link SlotRegistry} + {@link SolutionMode} 로
 * 분할됐지만, 19개 소비처의 점진 이주를 위해 어댑터({@link useArticleShellSlots})가 이 모양으로
 * 다시 평탄화해 제공한다. 신규 코드는 useSlotRegistry / useSolutionMode 를 직접 쓰는 것을 권장.
 *
 * @deprecated 점진 이주 대상 — useSlotRegistry() / useSolutionMode() 직접 사용 권장.
 */
export interface ArticleShellSlots {
  inShell: boolean;
  tocSlot: Slot;
  noteSlot: Slot;
  attendanceSlot: Slot;
  customLeftSlot: Slot;
  customRightSlot: Slot;
  subjectSlot: Slot;
  rightMode: "panels" | "solution" | "compose";
  solutionSlot: Slot;
  errorNoteSlot: Slot;
  activeSolutionId: string | null;
  wrongOpen: boolean;
  solutionCollapsed: boolean;
  enterSolution: (id: string) => void;
  exitSolution: () => void;
  setWrongOpen: (open: boolean) => void;
  setSplitRatio: (ratio: number) => void;
  attendanceVersion: number;
  refreshAttendance: () => void;
  scoreSlot: Slot;
  setScoreActive: (active: boolean) => void;
  problemNavSlot: Slot;
  quizControlsSlot: Slot;
  setControlsActive: (active: boolean) => void;
  shellHeaderSlot: Slot;
  headerActionSlot: Slot;
  restorationLeftSlot: Slot;
  restorationRightSlot: Slot;
  composeSlot: Slot;
  enterCompose: () => void;
  exitCompose: () => void;
}

/**
 * 호환 어댑터 — 분할된 {@link SlotRegistry} + {@link SolutionMode} 를 읽어 기존 평면
 * {@link ArticleShellSlots} 모양으로 평탄화해 반환한다. 기존 소비처는 이 훅만 쓰면 무수정으로
 * 동작한다. Provider 가 없으면 두 Context 모두 기본값을 반환해 SSR-safe 폴백(inShell:false)을 유지.
 */
export function useArticleShellSlots(): ArticleShellSlots {
  const registry = useSlotRegistry();
  const mode = useSolutionMode();
  const { left, right, header, attendance, sections } = registry;
  return React.useMemo<ArticleShellSlots>(
    () => ({
      inShell: registry.inShell,
      // left
      tocSlot: left.tocSlot,
      scoreSlot: left.scoreSlot,
      problemNavSlot: left.problemNavSlot,
      subjectSlot: left.subjectSlot,
      customLeftSlot: left.customLeftSlot,
      restorationLeftSlot: left.restorationLeftSlot,
      // right
      noteSlot: right.noteSlot,
      attendanceSlot: right.attendanceSlot,
      quizControlsSlot: right.quizControlsSlot,
      customRightSlot: right.customRightSlot,
      restorationRightSlot: right.restorationRightSlot,
      // header
      shellHeaderSlot: header.shellHeaderSlot,
      headerActionSlot: header.headerActionSlot,
      // attendance / sections
      attendanceVersion: attendance.attendanceVersion,
      refreshAttendance: attendance.refreshAttendance,
      setScoreActive: sections.setScoreActive,
      setControlsActive: sections.setControlsActive,
      // solution / compose mode
      rightMode: mode.rightMode,
      solutionSlot: mode.solutionSlot,
      errorNoteSlot: mode.errorNoteSlot,
      activeSolutionId: mode.activeSolutionId,
      wrongOpen: mode.wrongOpen,
      solutionCollapsed: mode.solutionCollapsed,
      enterSolution: mode.enterSolution,
      exitSolution: mode.exitSolution,
      setWrongOpen: mode.setWrongOpen,
      setSplitRatio: mode.setSplitRatio,
      composeSlot: mode.composeSlot,
      enterCompose: mode.enterCompose,
      exitCompose: mode.exitCompose,
    }),
    [registry.inShell, left, right, header, attendance, sections, mode],
  );
}
